import React, { useEffect, useState } from 'react'
import { useProjects } from '../../../Containers/construction/useHook'
import Swal from 'sweetalert2'
import { UrduInput } from '../../../Components/Shared/UrduInput'

const SubCategoryModal = ({ currentOurProjectCategory, toggleModal, onSuccess }) => {
    const { getParentProjectCategories, addSubCategory, updateSubCategory } = useProjects()
    const [parentProjectCategories, setParentProjectCategories] = useState([])
    const [formData, setFormData] = useState({
        name: '',
        subCategoryName_ur: '',
        description: '',
        description_ur: '',
        parentCategoryId: '',
        isActive: true
    })
    const [charCounts, setCharCounts] = useState({
        name: 0,
        description: 0
    })
    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)
    const [loading, setLoading] = useState(false)

    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return null;

        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            return imageUrl;
        }

        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        return imageUrl.startsWith('/') ? `${baseUrl}${imageUrl}` : `${baseUrl}/${imageUrl}`;
    }

    useEffect(() => {
        getParentProjectCategories(setParentProjectCategories)
        if (currentOurProjectCategory) {
            const formDataToSet = {
                name: currentOurProjectCategory.name || '',
                subCategoryName_ur: currentOurProjectCategory.subCategoryName_ur || '',
                description: currentOurProjectCategory.description || '',
                description_ur: currentOurProjectCategory.description_ur || '',
                parentCategoryId: currentOurProjectCategory.parent_id || currentOurProjectCategory.parentCategoryId || '',
                isActive: currentOurProjectCategory.isActive !== undefined ? currentOurProjectCategory.isActive : true
            }
            setFormData(formDataToSet)

            setCharCounts({
                name: formDataToSet.name.length,
                description: formDataToSet.description.length
            })

            if (currentOurProjectCategory.image_url) {
                setImagePreview(getImageUrl(currentOurProjectCategory.image_url))
            }
        }
    }, [])

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target

        const maxLengths = {
            name: 25,
            description: 70
        }

        if (type !== 'checkbox' && maxLengths[name] && value.length > maxLengths[name]) {
            return
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))

        if (type !== 'checkbox') {
            setCharCounts(prev => ({
                ...prev,
                [name]: value.length
            }))
        }
    }

    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            if (!file.type.startsWith('image/')) {
                Swal.fire("Error!", "Please select a valid image file.", "error")
                e.target.value = ''
                return
            }

            if (file.size > 5 * 1024 * 1024) {
                Swal.fire("Error!", "Image size should be less than 5MB.", "error")
                e.target.value = ''
                return
            }

            const img = new Image()
            img.onload = () => {
                if (img.width !== 450 || img.height !== 450) {
                    Swal.fire("Error!", "Image resolution must be exactly 450x450 pixels.", "error")
                    e.target.value = ''
                    return
                }

                setImageFile(file)
                const preview = URL.createObjectURL(file)
                setImagePreview(preview)
            }
            img.onerror = () => {
                Swal.fire("Error!", "Failed to load image. Please try again.", "error")
                e.target.value = ''
            }
            img.src = URL.createObjectURL(file)
        }
    }

    const removeImage = () => {
        if (imagePreview && imagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview)
        }
        setImageFile(null)
        setImagePreview(null)

        const fileInput = document.getElementById('sub-category-image')
        if (fileInput) {
            fileInput.value = ''
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!imageFile && !imagePreview) {
            Swal.fire({
                title: 'Error',
                text: 'Category image is required. Please upload an image.',
                icon: 'error'
            })
            return
        }

        setLoading(true)
        try {
            const formDataObj = new FormData()
            formDataObj.append('name', formData.name)
            if (formData.subCategoryName_ur) {
                formDataObj.append('subCategoryName_ur', formData.subCategoryName_ur)
            }
            formDataObj.append('description', formData.description)
            if (formData.description_ur) {
                formDataObj.append('description_ur', formData.description_ur)
            }
            formDataObj.append('parent_id', formData.parentCategoryId)

            if (imageFile) {
                formDataObj.append('image', imageFile)
            }

            if (currentOurProjectCategory) {
                await updateSubCategory(currentOurProjectCategory.id, formDataObj)
                Swal.fire({
                    title: 'Success',
                    text: 'Project sub category updated successfully',
                    icon: 'success'
                })
            } else {
                await addSubCategory(formDataObj)
                Swal.fire({
                    title: 'Success',
                    text: 'Project sub category added successfully',
                    icon: 'success'
                })
            }

            if (onSuccess) {
                onSuccess()
            }

            toggleModal()
            setFormData({
                name: '',
                subCategoryName_ur: '',
                description: '',
                description_ur: '',
                parentCategoryId: '',
                isActive: true
            })
            setCharCounts({
                name: 0,
                description: 0
            })
            setImageFile(null)
            setImagePreview(null)
        } catch (error) {
            console.error('Error saving project category:', error)
            Swal.fire({
                title: 'Error',
                text: error.response?.data?.message || 'Something went wrong',
                icon: 'error'
            })
        } finally {
            setLoading(false)
        }
    }

    const isFormValid = formData.name.trim() !== '' && formData.parentCategoryId !== '' && (imageFile || imagePreview)

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">
                        {currentOurProjectCategory ? 'Edit Project Sub Category' : 'Add Project Sub Category'}
                    </h2>
                    <button
                        onClick={toggleModal}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category Image *
                        </label>
                        <div className={`border border-dashed p-4 rounded-md ${!imageFile && !imagePreview ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}>
                            {imagePreview ? (
                                <div className="relative group">
                                    <img
                                        src={imagePreview}
                                        alt="Category preview"
                                        className="w-full h-32 object-cover rounded-lg"
                                    />
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        ×
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <input
                                        type="file"
                                        id="sub-category-image"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                    />
                                    <label
                                        htmlFor="sub-category-image"
                                        className="flex flex-col items-center justify-center cursor-pointer"
                                    >
                                        <div className="text-gray-500 mb-2">
                                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                        </div>
                                        <span className="text-sm text-gray-500">
                                            Click to upload image
                                        </span>
                                        <span className="text-xs text-red-400 mt-1 font-medium">
                                            * Required - PNG, JPG, GIF up to 5MB, 450x450 pixels
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>
                        {!imageFile && !imagePreview && (
                            <p className="text-red-500 text-xs mt-1">Category image is required</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sub Category Name * (Max 25 characters)
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            maxLength={25}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter sub category name"
                        />
                        <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-gray-500">
                                {formData.name.split(' ').filter(word => word.length > 0).length} words
                            </span>
                            <span className={`text-xs ${charCounts.name >= 20 ? 'text-red-500' : 'text-gray-500'}`}>
                                {charCounts.name}/25 characters
                            </span>
                        </div>
                        <UrduInput
                            label="ذیلی زمرہ کا نام"
                            name="subCategoryName_ur"
                            value={formData.subCategoryName_ur}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description (Max 70 characters)
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            maxLength={70}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            placeholder="Enter sub category description (optional)"
                        />
                        <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-gray-500">
                                {formData.description.split(' ').filter(word => word.length > 0).length} words
                            </span>
                            <span className={`text-xs ${charCounts.description >= 60 ? 'text-red-500' : 'text-gray-500'}`}>
                                {charCounts.description}/70 characters
                            </span>
                        </div>
                        <UrduInput
                            label="تفصیل"
                            name="description_ur"
                            value={formData.description_ur}
                            onChange={handleInputChange}
                            type="textarea"
                            rows={2}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Parent Category *
                        </label>
                        <select
                            name="parentCategoryId"
                            value={formData.parentCategoryId}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select parent category</option>
                            {parentProjectCategories.map((category) => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={toggleModal}
                            className="btn btn-reset-filter justify-center"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !isFormValid}
                            className={`btn justify-center ${
                                currentOurProjectCategory
                                    ? "btn-edit text-white"
                                    : "btn-primary"
                            } ${
                                loading || !isFormValid
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                            }`}
                        >
                            {loading
                                ? "Saving..."
                                : currentOurProjectCategory
                                  ? "Update"
                                  : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default SubCategoryModal